import {Drawer} from '@mantine/core';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {IconChevronRight, IconHistory} from '@tabler/icons-react';
import React, {useState} from 'react';
import type {InspectionDeviceHistoryEntryResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {Pill} from 'src/design/atoms/Pill';
import {useGetInspectionDeviceHistoryQuery} from 'src/redux/queries/inspection';
import {ComplianceStateEnumPill} from 'src/shared/constants/PillConstants';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {formatUtcShortDate} from 'src/shared/utils/formatters';
import {DeviceHistoryEntry} from 'src/pages/Modules/Inspections/shared/DeviceHistoryList';

export interface DeviceHistorySidebarProps {
  deviceId: number;
  /** Excludes this inspection's own entry from history — it's the record being viewed/edited, not "past". */
  inspectionId: number;
}

const RECENT_COUNT = 5;
const FIELD_PREVIEW_COUNT = 3;

const formatDate = (date?: Date) => formatUtcShortDate(date, 'Undated');

export const DeviceHistorySidebar: React.FC<DeviceHistorySidebarProps> = ({
  deviceId,
  inspectionId,
}) => {
  const {data, isFetching} = useGetInspectionDeviceHistoryQuery({
    deviceId,
    inspectionId,
  });
  const [showAll, setShowAll] = useState(false);
  const [openEntry, setOpenEntry] =
    useState<InspectionDeviceHistoryEntryResponse | null>(null);

  const entries = [...(data ?? [])]
    .filter(entry => entry.inspectionId !== inspectionId)
    .sort((a, b) => {
      const aTime = a.inspectionDate
        ? parseUtcDate(a.inspectionDate).getTime()
        : 0;
      const bTime = b.inspectionDate
        ? parseUtcDate(b.inspectionDate).getTime()
        : 0;
      return bTime - aTime;
    });

  const visibleEntries = showAll ? entries : entries.slice(0, RECENT_COUNT);
  const hiddenCount = entries.length - visibleEntries.length;

  return (
    <Stack gap="xs">
      <Group gap={6} wrap="nowrap">
        <IconHistory size={16} />
        <Text fw={600} size="sm">
          Device history
        </Text>
      </Group>
      <Text size="xs" c="dimmed">
        Past inspection records for this device
      </Text>

      {isFetching ? (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      ) : entries.length === 0 ? (
        <Text size="sm" c="dimmed">
          No prior inspections for this device.
        </Text>
      ) : (
        <Stack gap="xs">
          {visibleEntries.map(entry => (
            <HistoryCard
              key={entry.inspectionId}
              entry={entry}
              onClick={() => setOpenEntry(entry)}
            />
          ))}
          {hiddenCount > 0 && (
            <Button
              variant="subtle"
              size="xs"
              fullWidth
              onClick={() => setShowAll(true)}>
              View all history ({entries.length})
            </Button>
          )}
        </Stack>
      )}

      <Drawer
        opened={openEntry !== null}
        onClose={() => setOpenEntry(null)}
        position="right"
        size="md"
        title={
          openEntry
            ? `${openEntry.inspectionTypeName} · ${formatDate(openEntry.inspectionDate)}`
            : ''
        }>
        {openEntry && <DeviceHistoryEntry entry={openEntry} />}
      </Drawer>
    </Stack>
  );
};

const HistoryCard: React.FC<{
  entry: InspectionDeviceHistoryEntryResponse;
  onClick: () => void;
}> = ({entry, onClick}) => {
  const fieldPreview = entry.values.slice(0, FIELD_PREVIEW_COUNT);

  return (
    <UnstyledButton
      onClick={onClick}
      p="xs"
      style={{
        display: 'block',
        width: '100%',
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 4,
      }}>
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Group gap={6} wrap="nowrap">
          <Pill {...ComplianceStateEnumPill(entry.complianceState)} />
          <Text size="xs" c="dimmed">
            {formatDate(entry.inspectionDate)}
          </Text>
        </Group>
        <IconChevronRight size={14} />
      </Group>

      {entry.inspectorName && (
        <Text size="xs" c="dimmed" mt={2}>
          {entry.inspectorName}
        </Text>
      )}

      {fieldPreview.length > 0 && (
        <Stack gap={0} mt={4}>
          {fieldPreview.map(value => (
            <Text key={value.formFieldInvariantId} size="xs" truncate>
              {value.label}: {value.displayValue ?? value.value ?? '—'}
              {value.unit ? ` ${value.unit}` : ''}
            </Text>
          ))}
        </Stack>
      )}

      {entry.comments && (
        <Text size="xs" c="dimmed" mt={4} truncate>
          {entry.comments}
        </Text>
      )}
    </UnstyledButton>
  );
};
